import clsx from 'clsx';
import { memo, useEffect, useId, useRef, type ChangeEvent } from 'react';

import type { InputStatus } from '../../types/Status';

import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { Text } from '../Text/Text';
import './Checkbox.css';

export type CheckboxProps = {
  /** Accessible name for the checkbox input. */
  label: string;
  /** Whether the checkbox is selected. */
  selected: boolean;
  /** Called when the checkbox selection changes. */
  onChange: (selected: boolean) => void;
  disabled?: boolean;
  /** Supporting text under the visible label. */
  hint?: string;
  /** Partial-selection indicator for parent controls when children are mixed. */
  indeterminate?: boolean;
  name?: string;
  /** Whether the checkbox must be selected before the form can be submitted. */
  required?: boolean;
  /** Status styling (error, warning). */
  status?: InputStatus;
  /** Visible label next to the checkbox control. */
  text?: string;
  /** Submitted value when the checkbox is selected. */
  value?: string;
};

export const Checkbox = memo(function Checkbox({
  label,
  selected,
  onChange,
  disabled,
  hint,
  indeterminate = false,
  name,
  required,
  status,
  text,
  value,
}: CheckboxProps) {
  const generatedId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const hasCopy = Boolean(text) || Boolean(hint);
  const inputId =
    name !== undefined && value !== undefined
      ? `${name}-${value}`
      : generatedId;
  const hintId = hint ? `${inputId}-hint` : undefined;

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate && !selected;
    }
  }, [indeterminate, selected]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked);
  };

  const showIndeterminate = indeterminate && !selected;

  const control = (
    <span
      className={clsx(
        'eds-checkbox--control',
        status && `eds-checkbox--control-${status}`,
      )}
    >
      <input
        ref={inputRef}
        aria-describedby={hintId}
        aria-invalid={status === 'critical' || undefined}
        aria-label={label}
        aria-required={required || undefined}
        checked={selected}
        className="eds-checkbox--input"
        disabled={disabled}
        id={hasCopy ? inputId : undefined}
        name={name}
        required={required}
        type="checkbox"
        value={value}
        onChange={handleChange}
      />
      <span
        aria-hidden="true"
        className={clsx(
          'eds-checkbox--indicator',
          selected && 'eds-checkbox--indicator-selected',
          showIndeterminate && 'eds-checkbox--indicator-indeterminate',
          status === 'critical' && 'eds-checkbox--indicator-critical',
          status === 'cautious' && 'eds-checkbox--indicator-cautious',
        )}
      >
        {selected && <Icon icon="check" size="s" />}
        {showIndeterminate && <Icon icon="minus-remove" size="s" />}
      </span>
    </span>
  );

  if (!hasCopy) {
    return (
      <span
        className={clsx('eds-checkbox', disabled && 'eds-checkbox--disabled')}
      >
        {control}
      </span>
    );
  }

  return (
    <Layout
      align="start"
      className={clsx(
        'eds-checkbox',
        'eds-checkbox--labeled',
        disabled && 'eds-checkbox--disabled',
      )}
      spacing="xs"
    >
      {control}
      <Layout direction="column" flex="1" spacing="xs">
        {text ? (
          <Layout as="label" className="eds-checkbox--label" htmlFor={inputId}>
            <Text as="span" variant="body-m">
              {text}
            </Text>
          </Layout>
        ) : null}
        {hint ? (
          <Text as="p" id={hintId} tone="subdued" variant="body-s">
            {hint}
          </Text>
        ) : null}
      </Layout>
    </Layout>
  );
});

Checkbox.displayName = 'Checkbox';
